import {FormattedStore, Hour, LocationDetail, SelectedStore, StoreDetails} from '@interfaces/checkout'
import {getCurrentDistanceMeasureUnit, getCurrentLocale} from "@libs/utils/currentCountry";
import uniqBy from 'lodash/unionBy'


/**
 * Converts a 24-hour time string to a 12-hour format with AM/PM.
 * @param {string} time - The time in 24-hour format (e.g., "1300").
 * @returns {string} The time in 12-hour format with AM/PM (e.g., "01:00 PM").
 */
export function getDataTo12(time: string): string {
  if (!time) return time;
  const [hours, minutes] = [parseInt(time.slice(0, 2), 10), time.slice(2)];
  const period = hours < 12 ? 'AM' : 'PM';
  return `${(hours % 12 || 12).toString().padStart(2, '0')}${minutes} ${period}`;
}


/**
 * Gets the current day index based on the specified time zone.
 * @param {string} timeZone - The time zone (e.g., "Europe/Rome").
 * @returns {number} The index of the current day (0 for Sunday, 1 for Monday, etc.).
 */
export function getDayIndexByTimeZone(timeZone: string): number {
  const date = new Date().toLocaleString('en-US', { timeZone });
  return new Date(date).getDay();
}

/**
 * Formats the working hours for a given day.
 * @param {Object} item - The day object containing localizedDay, open, and close times.
 * @param {string} open - The opening time.
 * @param {string} close - The closing time.
 * @returns {string} An object containing the day name and formatted time.
 */
export function getWorkTime(item: { localizedDay: string }, open: string, close: string): string {
  const time = open && close ? `${open} - ${close}` : '';
  return `${item.localizedDay}: ${time}`;
}

/**
 * Gets the current working day for the store.
 * @param {LocationDetail} store - The store object.
 * @param {any[]} storeSchedule - The schedule of the store.
 * @param {any} currentDay - The current day object.
 * @returns {any} The working hours for the current day.
 */
export function currentWorkingDay(storeSchedule: any[], currentDay: any): any {
  return storeSchedule?.find(
    workingHoursDay => workingHoursDay.day === currentDay?.day
  );
}

/**
 * Checks if the store has inventory for the required items.
 * @param {LocationDetail} store - The store object.
 * @param {Record<string, number>} orderItemsData - The order items data.
 * @returns {boolean} True if the store has inventory, false otherwise.
 */

export function itemHasInventoryInStore(store: LocationDetail, orderItemsData: Record<string, string>): boolean {
  try {
    for (let key in orderItemsData) {
      const storeInventoryQuantity = parseInt(store.inventory[key])
      const localOrderQuantity = parseInt(orderItemsData[key])
      if (!storeInventoryQuantity || localOrderQuantity > storeInventoryQuantity) {
        return false;
      }
    }
    return true;
  } catch (e) {
    console.error('Error checking store inventory', e)
    return false
  }


}

/**
 * Checks if items are available today in the store.
 * @param {LocationDetail} store - The store object.
 * @param {Record<string, number>} orderItemsData - The order items data.
 * @returns {boolean} True if items are available today, false otherwise.
 */
export function itemsAvailableToday(store: LocationDetail, orderItemsData: Record<string, string>): boolean {
  return store.cncAvailable && itemHasInventoryInStore(store, orderItemsData);
}

/**
 * Checks if the store is closed.
 * @param {LocationDetail} store - The store object.
 * @returns {boolean} True if the store is closed, false otherwise.
 */
export function closed(store: LocationDetail): boolean {
  return (
    store.storeStatus === 'CLOSED' ||
    store.storeStatus === 'CLOSING_SOON' ||
    !store.storeStatus
  );
}

/**
 * Checks if the store is open.
 * @param {LocationDetail} store - The store object.
 * @returns {boolean} True if the store is open, false otherwise.
 */
export function open(store: LocationDetail): boolean {
  return store.storeStatus === 'OPEN' || store.storeStatus === 'OPENING_SOON';
}

/**
 * Checks if the store is open today.
 * @param {LocationDetail} store - The store object.
 * @param {any} currentDay - The current day object.
 * @returns {boolean} True if the store is open today, false otherwise.
 */
export function openToday(store: LocationDetail, currentDay: any): boolean {
  const { nextWorkingDay } = store;
  return currentDay?.day === nextWorkingDay?.day;
}

/**
 * Checks if the store is open tomorrow.
 * @param {LocationDetail} store - The store object.
 * @param {any} currentWorkingDay - The current working day object.
 * @param {string[]} weekDays - The list of week days.
 * @returns {boolean} True if the store is open tomorrow, false otherwise.
 */
export function openTomorrow(store: LocationDetail, currentWorkingDay: any, weekDays: string[]): boolean {
  const { nextWorkingDay } = store;
  const currentWorkingDayIndex = weekDays.indexOf(currentWorkingDay?.day);
  const nextWorkingDayIndex = weekDays.indexOf(nextWorkingDay?.day);
  return nextWorkingDayIndex % 7 === (currentWorkingDayIndex + 1) % 7;
}

/**
 * Gets the abbreviated name of the next open day.
 * @param {LocationDetail} store - The store object.
 * @param {any} currentWorkingDay - The current working day object.
 * @param {boolean} openToday - Whether the store is open today.
 * @returns {string} The abbreviated name of the next open day.
 */
export function openDayNameText(store: LocationDetail, currentWorkingDay: any, openToday: boolean): string {
  try {
    const { nextWorkingDay } = store;
    if (openToday) {
      return currentWorkingDay?.day?.slice(0, 3);
    } else {
      return nextWorkingDay?.day?.slice(0, 3);
    }
  }catch (e) {
    console.error('Error getting open day name text', e)
    return ''
  }

}

/**
 * Gets the text indicating when the store opens.
 * @param {boolean} openToday - Whether the store is open today.
 * @param {boolean} openTomorrow - Whether the store is open tomorrow.
 * @returns {string} The text indicating when the store opens.
 */
export function opensDayText(openToday: boolean, openTomorrow: boolean): string {
  if (openToday) {
    return 'today';
  } else if (openTomorrow) {
    return 'tomorrow';
  } else {
    return '';
  }
}

/**
 * Gets the opening hour of the store.
 * @param {LocationDetail} store - The store object.
 * @param {any} currentWorkingDay - The current working day object.
 * @param {boolean} openToday - Whether the store is open today.
 * @param {boolean} is12HourClockCountry - Whether the country uses a 12-hour clock format.
 * @param {Function} getDataTo12 - Function to convert time to 12-hour format.
 * @returns {string} The opening hour of the store.
 */
export function openHour(
  store: LocationDetail,
  currentWorkingDay: any,
  openToday: boolean,
  is12HourClockCountry: boolean,
  getDataTo12: (time: string) => string
): string {
  try {
    const { nextWorkingDay } = store;
    if (openToday) {
      return is12HourClockCountry
        ? getDataTo12(currentWorkingDay?.open?.slice(0, 2))
        : currentWorkingDay?.open;
    } else {
      return is12HourClockCountry
        ? getDataTo12(nextWorkingDay?.open)
        : nextWorkingDay?.open;
    }
  } catch (e) {
    console.error('Error getting open hour', e)
    return ''
  }

}

/**
 * Gets the store status message.
 * @param {LocationDetail} store - The store object.
 * @param {boolean} closed - Whether the store is closed.
 * @param {boolean} open - Whether the store is open.
 * @returns {string} The store status message.
 */
export function storeStatusMessage(store: LocationDetail, closed: boolean, open: boolean): string {
  if (closed) {
    return 'CLOSED';
  } else if (open) {
    return 'NOW_OPEN';
  } else {
    return store.storeStatus;
  }
}

/**
 * Formats the weekly hours schedule for a store.
 * @param {any[]} storeSchedule - The schedule of the store (array of days with open/close times).
 * @param {boolean} is12HourClockCountry - Whether the country uses a 12-hour clock format.
 * @param {Function} getWorkTime - Function to format the working hours for a day.
 * @param {Function} getDataTo12 - Function to convert time to 12-hour format.
 * @returns {string[]} An array of formatted working hours for each day.
 */
export function formatWeeklyHoursSchedule(
  storeSchedule: any[],
  is12HourClockCountry: boolean,
  getWorkTime: (day: any, open: string, close: string) => string,
  getDataTo12: (time: string) => string
): string[] {
  return storeSchedule.map(day => {
    if (is12HourClockCountry) {
      return getWorkTime(day, getDataTo12(day.open), getDataTo12(day.close));
    } else {
      return getWorkTime(day, day.open, day.close);
    }
  });
}

/**
 * Converts a phone number into an international format.
 * @param {string} phoneNumber - The phone number to format.
 * @returns {string} The formatted phone number.
 */
export const convertPhoneNumberToInternationalFormat = (phoneNumber: string):string => {
  const onlyDigitalCharacters = phoneNumber.trim().replace(/[-()]/g, '');

  if (onlyDigitalCharacters.length === 10) {
    return `(${onlyDigitalCharacters.substr(0, 3)}) ${onlyDigitalCharacters.substr(3, 3)}-${onlyDigitalCharacters.substr(6)}`;
  }

  return phoneNumber;
}
/**
 * Retrieves the next working hours for the store.
 * @param {Object} store - The store object containing hours and timeZone.
 * @param currentDay - The current day object containing localizedDay, open, and close times.
 * @param is12HourClockCountry
 * @returns {Object|string} The formatted working hours or an empty string if unavailable.
 */
export const getStoreNextWorkingHours = (store: LocationDetail,currentDay: Hour,is12HourClockCountry:boolean): string => {
  const { hours, timeZone } = store
  if (!hours || !timeZone) return ''
  return is12HourClockCountry
    ? getWorkTime(
      currentDay,
      getDataTo12(currentDay.open),
      getDataTo12(currentDay.close)
    )
    : getWorkTime(currentDay, currentDay.open, currentDay.close)
}

/**
 * Determines if the current country's time format is 24-hour.
 * @returns {boolean} True if the current country's time format is 24-hour, false otherwise.
 */
export function is24HourFormat(): boolean {
  const currentLocale = getCurrentLocale();
  const testDate = new Date('2023-01-01T13:00:00'); // Example time in the afternoon
  const formattedTime = new Intl.DateTimeFormat(currentLocale, {
    hour: 'numeric',
    minute: 'numeric',
  }).format(testDate);

  // If the formatted time contains "13", it's a 24-hour format
  return formattedTime.includes('13');
}

/**
 * Determines the order type for a store based on its availability and conditions.
 * @param {LocationDetail} store - The store object.
 * @param {boolean} isEngraved - Whether the item is engraved.
 * @param {boolean} isGiftWrapped - Whether the item is gift-wrapped.
 * @param {boolean} isRemix - Whether the item is a remix.
 * @returns {string} The order type ('CNC' or 'STS').
 */
export function getOrderType(
  store: FormattedStore,
  isEngraved: boolean,
  isGiftWrapped: boolean,
  isRemix: boolean
): string {
  return store.itemsAvailableToday &&
  !isEngraved &&
  !isGiftWrapped &&
  !isRemix
    ? 'CNC'
    : 'STS';
}

/**
 * Formats a list of stores with detailed information for display.
 * @param {LocationDetail[]} stores - The list of store objects to format.
 * @param {string[]} weekDays - An array of week day names (e.g., ["Sunday", "Monday", ...]).
 * @param {Record<string, number>} orderItemsData - The order items data, mapping item IDs to quantities.
 * @param {boolean} is12HourClockCountry - Whether the country uses a 12-hour clock format.
 * @returns {FormattedStore[]} An array of formatted store objects with detailed information.
 */
export function formatStoresList(
  stores: LocationDetail[],
  weekDays: string[],
  orderItemsData: Record<string, string>,
  is12HourClockCountry: boolean
): FormattedStore[] {
  return stores?.map((store: LocationDetail) => {
    const { hours, timeZone } = store
    const storeSchedule = store?.hours.slice() || []
    const currentDay = hours[getDayIndexByTimeZone(timeZone)]
    const currentWorkingDay = storeSchedule?.find(
      workingHoursDay => workingHoursDay.day === currentDay?.day
    )
    const openTodayFlag = openToday(store, currentDay);
    const openTomorrowFlag = openTomorrow(store, currentWorkingDay, weekDays);
    const itemsAreAvailableToday = itemsAvailableToday(store, orderItemsData);

    return {
      id: store.id,
      zip: store.zip,
      city: store.city,
      state: store.state,
      address: store.address,
      phone: store.phone,
      displayAddress: store.displayAddress,
      itemsAvailableToday: itemsAreAvailableToday,
      distanceMiles: store.distanceMiles,
      distanceKilometers: store.distanceKilometers,
      hoursMap: store.hoursMap,
      storeNextWorkingHours: getStoreNextWorkingHours(store,currentDay,is12HourClockCountry),
      storeStatusMessage: storeStatusMessage(store, closed(store), open(store)),
      storeStatus: store.storeStatus,
      storeSchedule: storeSchedule,
      opensDayText: opensDayText(openTodayFlag, openTomorrowFlag),
      inventory: store.inventory,
      favoriteStore: store.favoriteStore,
      defaultFavoriteStore: store.defaultFavoriteStore,
      openDayNameText: openDayNameText(store, currentWorkingDay, openTodayFlag),
      openHour: openHour(store, currentWorkingDay, openTodayFlag, is12HourClockCountry, getDataTo12),
      formattedPhoneNumber: convertPhoneNumberToInternationalFormat(store?.phone || ''),
      currentWorkingDay,
      nextWorkingDay: store.nextWorkingDay,
      closed: closed(store),
      directionURL: store.directionURL,
      storeWeeklyHoursSchedule: formatWeeklyHoursSchedule(storeSchedule, is12HourClockCountry, getWorkTime, getDataTo12),
      timeZone: store.timeZone,
      longitude: store.longitude,
      latitude: store.latitude,
    };
  });
}

export const sortStoresByDistance = (stores:(LocationDetail | FormattedStore)[] = []):(LocationDetail | FormattedStore )[] => {
  return stores.sort((a, b) =>
    getCurrentDistanceMeasureUnit() === 'km'
      ? parseFloat(a.distanceKilometers ?? '0') - parseFloat(b.distanceKilometers ?? '0')
      : parseFloat(a.distanceMiles ?? '0') - parseFloat(b.distanceMiles ?? '0')
  )
}
/**
 * Moves a store with a specific ID to the front of the stores array if it exists.
 * @param {Array<LocationDetail | FormattedStore>} stores - The array of stores.
 * @param physicalStoreId - the physical store id
 * @returns {Array<LocationDetail | FormattedStore>} A new array with the specified store moved to the front if found.
 */
export function prioritizeStoreById<T extends LocationDetail | FormattedStore>(
  stores: T[],
  physicalStoreId: string
): T[] {
  if (!stores?.length || !physicalStoreId) return stores || [];

  const storesCopy = [...stores];
  const storeIndex = storesCopy.findIndex(store => store.id === physicalStoreId);

  if (storeIndex > -1) {
    // Remove the store from its current position and add it to the beginning
    const [store] = storesCopy.splice(storeIndex, 1);
    return [store, ...storesCopy];
  }

  return storesCopy;
}

export const sortStores = (stores: StoreDetails,orderItemsData:Record<any, any>):(LocationDetail | FormattedStore)[] => {
  const storesWithInventory: LocationDetail[] = []
  const emptyLocations: LocationDetail[] = stores?.emptyLocations || []
  stores?.locationDetails?.forEach((store) => {
    if (store?.cncAvailable && itemHasInventoryInStore(store,orderItemsData)) {
      storesWithInventory.push(store)
    } else if (store?.stsAvailable) {
      emptyLocations.push(store)
    }
  })
  //sort the stores with inventory by distance
  sortStoresByDistance(storesWithInventory)
  //sort the stores without inventory by distance and append them to the end of the list
  const emptyFilterStores = sortStoresByDistance(emptyLocations.filter((store) => store.stsAvailable))

  const allStores = uniqBy([...storesWithInventory, ...emptyFilterStores], 'id')

  // separates stores based on preference criteria
  const defaultFavorite = allStores.find(store => store.defaultFavoriteStore)
  const otherFavorites = allStores.filter(store => store.favoriteStore && !store.defaultFavoriteStore)
  const noFavorites = allStores.filter(store => !store.favoriteStore && !store.defaultFavoriteStore)

  return [
    ...(defaultFavorite ? [defaultFavorite] : []),
    ...otherFavorites,
    ...noFavorites
  ]
}

/** Finds the index of the store matching the selectedStore id.
 Moves it to the first position*/
export const reshuffleStoresList = (
  locationDetails: (LocationDetail | FormattedStore)[] = [],
  selectedStore: SelectedStore,
  maxStoresToShow: number | null = 3
): LocationDetail[] | FormattedStore[] | null => {
  try {
    if (!locationDetails?.length) return []

    const reshuffled = [...locationDetails]
    const selectedStoreId = selectedStore?.physicalStoreId
    if (!selectedStoreId) return reshuffled

    const selectedStorePos = reshuffled.findIndex(item => item.id === selectedStoreId)
    if (selectedStorePos > 0) {
      reshuffled.unshift(reshuffled.splice(selectedStorePos, 1)[0])
    }

    return reshuffled
  } catch (e) {
    console.error('Error reshuffling store list', e)
    return null
  }
}
